/*
  Ported from src/components/MeshSimulator.tsx — pure SVG + pointer-events node
  graph simulator. Node state lives in a plain closure array (replacing
  useState), re-rendered imperatively on every mutation. Same interaction
  rules: click empty space to add, drag to move, click-without-drag to remove,
  BFS reachability from the fixed "self" node.
*/
(function () {
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var VB_W = 800;
  var VB_H = 380;
  var RANGE = 190;
  var MAX_NODES = 10;
  var MIN_NODES = 2;
  var CLICK_THRESHOLD = 6;

  function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  function reachableSet(nodes, edges) {
    var self = nodes.find(function (n) { return n.isSelf; });
    if (!self) return new Set();
    var adjacency = new Map();
    nodes.forEach(function (n) { adjacency.set(n.id, []); });
    edges.forEach(function (pair) {
      adjacency.get(pair[0].id).push(pair[1].id);
      adjacency.get(pair[1].id).push(pair[0].id);
    });
    var visited = new Set([self.id]);
    var queue = [self.id];
    while (queue.length) {
      var current = queue.shift();
      (adjacency.get(current) || []).forEach(function (neighbor) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      });
    }
    return visited;
  }

  class OblivionMeshSimulator extends HTMLElement {
    connectedCallback() {
      this.svg = this.querySelector('[data-mesh-svg]');
      this.countEl = this.querySelector('[data-mesh-count]');
      this.statusEl = this.querySelector('[data-mesh-status]');

      this.nextId = 6;
      this.nodes = [
        { id: 1, x: 400, y: 190, isSelf: true },
        { id: 2, x: 220, y: 120 },
        { id: 3, x: 260, y: 280 },
        { id: 4, x: 560, y: 140 },
        { id: 5, x: 590, y: 260 },
      ];
      this.enteredIds = new Set(this.nodes.map(function (n) { return n.id; }));
      this.drag = null;

      this.svg.addEventListener('pointerdown', this.onBackgroundDown.bind(this));
      this.svg.addEventListener('pointermove', this.onPointerMove.bind(this));

      this.render();
    }

    toSvgPoint(clientX, clientY) {
      var rect = this.svg.getBoundingClientRect();
      var x = ((clientX - rect.left) / rect.width) * VB_W;
      var y = ((clientY - rect.top) / rect.height) * VB_H;
      return {
        x: Math.max(20, Math.min(VB_W - 20, x)),
        y: Math.max(20, Math.min(VB_H - 20, y)),
      };
    }

    onBackgroundDown(e) {
      if (e.target.dataset && e.target.dataset.node) return;
      if (this.nodes.length >= MAX_NODES) return;
      var p = this.toSvgPoint(e.clientX, e.clientY);
      var id = this.nextId++;
      this.nodes.push({ id: id, x: p.x, y: p.y });
      this.render();
    }

    onNodeDown(e, id) {
      e.stopPropagation();
      e.target.setPointerCapture(e.pointerId);
      this.drag = { id: id, startX: e.clientX, startY: e.clientY, moved: false };
    }

    onPointerMove(e) {
      var drag = this.drag;
      if (!drag) return;
      if (Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) > CLICK_THRESHOLD) {
        drag.moved = true;
      }
      var p = this.toSvgPoint(e.clientX, e.clientY);
      var node = this.nodes.find(function (n) { return n.id === drag.id; });
      if (node) {
        node.x = p.x;
        node.y = p.y;
        this.render();
      }
    }

    onNodeUp(id) {
      var drag = this.drag;
      this.drag = null;
      if (!drag || drag.moved) return;
      var target = this.nodes.find(function (n) { return n.id === id; });
      if (!target || target.isSelf) return;
      if (this.nodes.length <= MIN_NODES) return;
      this.nodes = this.nodes.filter(function (n) { return n.id !== id; });
      this.render();
    }

    render() {
      var nodes = this.nodes;
      var edges = [];
      for (var i = 0; i < nodes.length; i++) {
        for (var j = i + 1; j < nodes.length; j++) {
          if (distance(nodes[i], nodes[j]) <= RANGE) edges.push([nodes[i], nodes[j]]);
        }
      }
      var reachable = reachableSet(nodes, edges);
      var isolated = nodes.filter(function (n) { return !n.isSelf && !reachable.has(n.id); });
      var meshIntact = isolated.length === 0;

      while (this.svg.firstChild) this.svg.removeChild(this.svg.firstChild);

      edges.forEach(function (pair) {
        var a = pair[0];
        var b = pair[1];
        var bothReachable = reachable.has(a.id) && reachable.has(b.id);
        var line = document.createElementNS(SVG_NS, 'line');
        line.setAttribute('x1', a.x);
        line.setAttribute('y1', a.y);
        line.setAttribute('x2', b.x);
        line.setAttribute('y2', b.y);
        line.setAttribute('stroke', bothReachable ? '#E52E06' : '#F4F4F0');
        line.setAttribute('stroke-opacity', bothReachable ? '0.5' : '0.15');
        line.setAttribute('stroke-width', '1');
        this.svg.appendChild(line);
      }, this);

      var self = this;
      nodes.forEach(function (n) {
        var cut = !n.isSelf && !reachable.has(n.id);
        var g = document.createElementNS(SVG_NS, 'g');

        if (n.isSelf) {
          var pulse = document.createElementNS(SVG_NS, 'circle');
          pulse.setAttribute('cx', n.x);
          pulse.setAttribute('cy', n.y);
          pulse.setAttribute('r', '22');
          pulse.setAttribute('fill', 'none');
          pulse.setAttribute('stroke', '#E52E06');
          pulse.setAttribute('stroke-opacity', '0.3');

          var animR = document.createElementNS(SVG_NS, 'animate');
          animR.setAttribute('attributeName', 'r');
          animR.setAttribute('values', '14;26;14');
          animR.setAttribute('dur', '2.4s');
          animR.setAttribute('repeatCount', 'indefinite');
          pulse.appendChild(animR);

          var animOpacity = document.createElementNS(SVG_NS, 'animate');
          animOpacity.setAttribute('attributeName', 'stroke-opacity');
          animOpacity.setAttribute('values', '0.5;0;0.5');
          animOpacity.setAttribute('dur', '2.4s');
          animOpacity.setAttribute('repeatCount', 'indefinite');
          pulse.appendChild(animOpacity);

          g.appendChild(pulse);
        }

        var circle = document.createElementNS(SVG_NS, 'circle');
        circle.setAttribute('data-node', n.id);
        circle.setAttribute('cx', n.x);
        circle.setAttribute('cy', n.y);
        circle.setAttribute('r', n.isSelf ? 10 : 7);
        circle.setAttribute('fill', n.isSelf ? '#E52E06' : cut ? '#3A3C3E' : '#F4F4F0');
        circle.setAttribute('stroke', cut ? '#E52E06' : 'none');
        circle.setAttribute('stroke-width', cut ? '1.5' : '0');
        circle.style.cursor = 'pointer';
        circle.addEventListener('pointerdown', function (e) { self.onNodeDown(e, n.id); });
        circle.addEventListener('pointerup', function () { self.onNodeUp(n.id); });
        g.appendChild(circle);

        self.svg.appendChild(g);

        if (!self.enteredIds.has(n.id)) {
          self.enteredIds.add(n.id);
          if (window.gsap) {
            window.gsap.fromTo(
              circle,
              { scale: 0, transformOrigin: '50% 50%' },
              { scale: 1, duration: 0.5, ease: 'back.out(2)' }
            );
          }
        }
      });

      this.countEl.textContent = nodes.length + (nodes.length > 1 ? ' pilotes sur le réseau' : ' pilote sur le réseau');
      this.statusEl.textContent = meshIntact
        ? '● Mesh intact, tous connectés'
        : '● ' + isolated.length + (isolated.length > 1 ? ' pilotes hors de portée' : ' pilote hors de portée');
      this.statusEl.classList.toggle('is-intact', meshIntact);
    }
  }

  if (!customElements.get('oblivion-mesh-simulator')) {
    customElements.define('oblivion-mesh-simulator', OblivionMeshSimulator);
  }
})();
